import getBeneficiaryPersonalStatus from './getBeneficiaryPersonalStatus';
import getCapitalDepositStepStatus from './getCapitalDepositStepStatus';
import getDraftStatutesStepStatus from './getDraftStatutesStepStatus';
import getIsYetToSelectPlan from './getIsYetToSelectPlan';
import getKycStepStatus from './getKycStepStatus';
import getMultiKycStatus from './getMultiKycStatus';
import getShineStartStepStatus from './getShineStartStepStatus';
import { ContextSpecificStepStatus, VerificationStatusHook } from './types';

const getHasRefusedDocs = (
  stepCompletionStatuses: ContextSpecificStepStatus[],
): boolean =>
  stepCompletionStatuses.find((step) => step === 'error') !== undefined;

const isRemainingStep = (step: ContextSpecificStepStatus) => step === 'waiting';

const totalKycStepsRemaining = (
  stepCompletionStatuses: ContextSpecificStepStatus[],
): number => stepCompletionStatuses.filter(isRemainingStep).length;

const useVerificationStatus: VerificationStatusHook = (
  verificationQueryResult,
  isMissingProofOfAddress,
) => {
  const { data, isPrimaryApplicant } = verificationQueryResult;
  if (
    data === undefined ||
    isMissingProofOfAddress === undefined ||
    isPrimaryApplicant === undefined
  ) {
    return undefined;
  }
  const {
    viewer: {
      companyUser: {
        isAllowedToSkipPOA,
        kycAddressStatus: baseKycAddressStatus,
        kycIdStatus: baseKycIdStatus,
      },
    },
  } = data;

  let kycAddressStatus: ContextSpecificStepStatus;
  if (isAllowedToSkipPOA) {
    kycAddressStatus = 'not-shown';
  } else if (isMissingProofOfAddress) {
    kycAddressStatus = 'waiting';
  } else {
    kycAddressStatus = getKycStepStatus(baseKycAddressStatus);
  }

  const kycIdStatus = getKycStepStatus(baseKycIdStatus);
  const shineStartStatus = getShineStartStepStatus(data);
  const capitalDepositStatus = getCapitalDepositStepStatus(
    verificationQueryResult,
  );
  const draftStatutesStatus = getDraftStatutesStepStatus(data);
  const stepCompletionStatuses = [
    kycIdStatus,
    kycAddressStatus,
    shineStartStatus,
    capitalDepositStatus,
    // Note that the multi-KYC/beneficiaries step is not included in the calculation
    // of how many steps remain - nor does its status block the enabling of the submit button
  ];
  const stepsRemaining = totalKycStepsRemaining(stepCompletionStatuses);
  const hasRefusedDocs = getHasRefusedDocs(stepCompletionStatuses);
  const shouldSubmitBeEnabled =
    stepsRemaining === 0 && hasRefusedDocs === false;
  const isYetToSelectPlan = getIsYetToSelectPlan(data);
  const multiKycStatus = getMultiKycStatus(data);
  const beneficiaryPersonalStatus = getBeneficiaryPersonalStatus(
    data,
    isPrimaryApplicant,
  );

  const isCurrentUserComplete =
    data.companyLegalBeneficiaries?.find(({ isCurrentUser }) => isCurrentUser)
      ?.onboardingStatus === 'VALIDATED';

  return {
    beneficiaryPersonalStatus,
    capitalDepositStatus,
    draftStatutesStatus,
    hasRefusedDocs,
    isCurrentUserComplete,
    isYetToSelectPlan,
    kycAddressStatus,
    kycIdStatus,
    shineStartStatus,
    shouldSubmitBeEnabled,
    stepsRemaining,
    ...multiKycStatus,
  };
};

export default useVerificationStatus;
